import { create } from 'zustand';
import {
  listNotifications,
  getUnreadCount,
  markAsRead as apiMarkAsRead,
  markAllRead as apiMarkAllRead,
} from '../api/notifications';
import type { Notification } from '../api/notifications';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isOpen: boolean;
  loading: boolean;
  pollIntervalId: ReturnType<typeof setInterval> | null;

  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  togglePanel: () => void;
  closePanel: () => void;
  startPolling: () => void;
  stopPolling: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isOpen: false,
  loading: false,
  pollIntervalId: null,

  fetchNotifications: async () => {
    try {
      set({ loading: true });
      const notifications = await listNotifications({ limit: 50 });
      const unreadCount = notifications.filter((n) => !n.is_read).length;
      set({ notifications, unreadCount, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const count = await getUnreadCount();
      set({ unreadCount: count });
    } catch (err) {
      console.error('[notif] request failed:', err);
    }
  },

  markAsRead: async (id: string) => {
    try {
      await apiMarkAsRead(id);
      set((s) => ({
        notifications: s.notifications.map((n) =>
          n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
        ),
        unreadCount: Math.max(0, s.unreadCount - 1),
      }));
    } catch {
      console.error('Failed to mark notification as read');
    }
  },

  markAllAsRead: async () => {
    try {
      await apiMarkAllRead();
      set((s) => ({
        notifications: s.notifications.map((n) => ({
          ...n,
          is_read: true,
          read_at: n.read_at || new Date().toISOString(),
        })),
        unreadCount: 0,
      }));
    } catch {
      console.error('Failed to mark all as read');
    }
  },

  togglePanel: () => {
    const { isOpen } = get();
    if (!isOpen) get().fetchNotifications();
    set({ isOpen: !isOpen });
  },

  closePanel: () => set({ isOpen: false }),

  startPolling: () => {
    const existing = get().pollIntervalId;
    if (existing) return;

    // Initial fetch
    get().fetchUnreadCount();

    // Poll every 30 seconds
    const id = setInterval(() => {
      get().fetchUnreadCount();
    }, 30000);
    set({ pollIntervalId: id });
  },

  stopPolling: () => {
    const id = get().pollIntervalId;
    if (id) {
      clearInterval(id);
      set({ pollIntervalId: null });
    }
  },
}));
